// TEMPORARILY DISABLED: forceConsoleOverride - using normal console for now
// import './utils/forceConsoleOverride';
import './utils/suppressBrowserWarnings';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';

import App from './App';
import theme from './theme'; // Import our enhanced theme
import './index.css';

import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import initializeLogger from './utils/loggerInitializer';
import { hasLocalStorage } from './utils/storageSafe';
import logger from './utils/logger';
import { ensurePushSubscription } from './utils/notifications/ensurePushSubscription';
import { shouldReloadForServiceWorkerActivation } from './utils/serviceWorkerUpdatePolicy';

// Suppress ResizeObserver warnings (harmless browser quirk)
if (typeof window !== 'undefined') {
  const suppressResizeObserver = (e) => {
    const msg = e.message || e;
    if (msg && typeof msg === 'string' && msg.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation && e.stopImmediatePropagation();
      e.preventDefault && e.preventDefault();
      return true;
    }
  };

  window.addEventListener('error', suppressResizeObserver);

  // Also patch window.onerror to be sure
  const originalOnError = window.onerror;
  window.onerror = (msg, url, line, col, error) => {
    if (msg && msg.includes('ResizeObserver loop')) return true;
    return originalOnError ? originalOnError(msg, url, line, col, error) : false;
  };
}

// ✅ REMOVED: Early console silencing - let logger system and productionDebug handle it
// This allows the logger system and productionDebug to properly control console output

// src/index.js – prototype with robust localStorage handling
// ----------------------------------------------------------
// • Uses a *safeShouldInitLogger()* helper that never throws
// • Falls back to rendering the app even if the logger fails
// • Adds clear console markers so you can trace the execution

// Note: productionDebug is imported AFTER logger initialization (see below)

// ----------------------------------------------------------
// 1️⃣  Theme & global version flag
// ----------------------------------------------------------
// Theme is now imported from ./theme.js with enhanced styling
window.CODE_VERSION = 'XYZ'; // <— replace during CI/CD if desired

// Index.js initialized (no logging)

// Add manual cache clearing function for testing
window.clearPWACache = async () => {
  try {
    console.log('🧹 Manually clearing PWA cache...');

    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('✅ Service Worker caches cleared');
    }

    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();
    console.log('✅ Local storage cleared');

    // Clear IndexedDB
    if ('indexedDB' in window && 'databases' in indexedDB) {
      const databases = await indexedDB.databases();
      await Promise.all(databases.map(db => indexedDB.deleteDatabase(db.name)));
      console.log('✅ IndexedDB cleared');
    }

    console.log('🧹 All PWA storage cleared! Reload the page to see the effect.');
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
  }
};

// ----------------------------------------------------------
// 2️⃣  Plain helper to mount the React tree
// ----------------------------------------------------------
function renderApp() {
  // renderApp called (no logging)

  ReactDOM.render(
    <ThemeProvider theme={theme}>
      <HelmetProvider>
        <Router>
          <App />
        </Router>
      </HelmetProvider>
    </ThemeProvider>,
    document.getElementById('root')
  );

  // SW logic
  const isLocalDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const explicitServiceWorkerOptIn = process.env.REACT_APP_ENABLE_SW === 'true';
  const shouldRegisterServiceWorker =
    explicitServiceWorkerOptIn ||
    ['beta.fusesoul.com', 'staging.fusesoul.com'].includes(window.location.hostname) ||
    (process.env.NODE_ENV === 'production' && !isLocalDevHost);

  if (shouldRegisterServiceWorker) {
    serviceWorkerRegistration.register({
      onSuccess: (reg) => {
        console.log('🚀 SW registered:', reg.scope);
        // reg is the ServiceWorkerRegistration object, not a Container, so it has no .ready promise.
        // pass it directly.
        ensurePushSubscription(reg).catch(console.error);

        // Register service worker message handler for cache clearing
        import('./utils/serviceWorkerHandler').then(({ registerServiceWorkerHandler }) => {
          registerServiceWorkerHandler();
          console.log('📡 Service Worker message handler registered');

          // ✅ Belt-and-suspenders: reload on controller change to ensure fresh app (PWA only)
          if ('serviceWorker' in navigator) {
            if (shouldReloadForServiceWorkerActivation()) {
              navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (window.__fsSwReloadPending) {
                  console.log('[SW] Reload already pending, skipping duplicate controllerchange reload');
                  return;
                }
                window.__fsSwReloadPending = true;
                console.log('[SW] New controller activated — reloading (PWA only)');
                window.location.reload();
              });
            }
          }

          // ✅ Prompt if a worker is already waiting (web UX polish)
          if (reg.waiting) {
            console.log('[SW Registration] Found waiting worker in onSuccess, dispatching update event');
            window.dispatchEvent(new CustomEvent('swUpdateFound', { detail: { registration: reg } }));
          }

          // ✅ Make forceSWUpdate available globally for testing
          import('./serviceWorkerRegistration').then(({ forceSWUpdate }) => {
            window.forceSWUpdate = forceSWUpdate;
            console.log('🔧 forceSWUpdate function available globally for testing');
          });
        }).catch(err => {
          console.warn('⚠️ Failed to register SW message handler:', err);
        });
      },
    });
  } else {
    if (isLocalDevHost) {
      console.info('[index] Service worker disabled on localhost to avoid stale cached bundles during development.');
    }
    serviceWorkerRegistration.unregister();
  }
}

// ----------------------------------------------------------
// 3️⃣  Decide safely whether we *can* use localStorage
// ----------------------------------------------------------
function safeShouldInitLogger() {
  // Enable logger initialization in both development and production
  try {
    return hasLocalStorage();         // <- never throws, but extra guard anyway
  } catch (err) {
    console.warn('[index] localStorage probe threw – treating as unavailable');
    return false;
  }
}

// ----------------------------------------------------------
// 4️⃣  Flow control
// ----------------------------------------------------------
if (safeShouldInitLogger()) {
  // Attempting initializeLogger (no logging)

  initializeLogger()
    // Never let a logger failure block the UI
    .catch(err => {
      logger.error('initializeLogger() failed – continuing without logger', err);
    })
    .then(() => {
      // ✅ CRITICAL: Import productionDebug AFTER logger initializes
      // This ensures our debug script can override the logger's console suppression
      return import('./utils/productionDebug');
    })
    .catch(err => {
      // Silently fail if productionDebug import fails
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Failed to load productionDebug:', err);
      }
    })
    .finally(renderApp);
} else {
  logger.info('@@@ INDEX.JS – skipping logger init (no LS) @@@');
  // Still load productionDebug even if logger init is skipped
  import('./utils/productionDebug').catch(() => { });
  renderApp();
}
